import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  InputBase,
  alpha,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  AccountCircle,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Help as HelpIcon,
  Brightness4 as ThemeIcon,
} from '@mui/icons-material';
import NotificationCenter from './NotificationCenter';
import GlobalSearch from './GlobalSearch';

const EnhancedAppBar = ({ 
  onMenuToggle, 
  profile, 
  onLogout, 
  darkMode, 
  onThemeToggle,
  token,
  title,
  onQuickAction,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleProfileMenuClose();
  };

  const handleLogout = () => {
    onLogout?.();
    handleProfileMenuClose();
  };

  const getPageTitle = () => {
    if (title) return title;
    
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    
    const titleMap = {
      'dashboard': 'Dashboard',
      'bookings': 'Bookings',
      'calendar': 'Calendar',
      'profile': 'Profile',
      'pricing': 'Pricing',
      'reviews': 'Reviews',
      'payments': 'Payments',
      'settings': 'Settings',
      'team': 'Team',
      'analytics': 'Analytics',
      'trucks': 'Truck Rental',
      'single-item': 'Single Items',
      'hotshot': 'Hotshot Services',
    };
    
    return titleMap[lastSegment] || 'Mover Portal';
  };

  const getServiceBadges = () => {
    if (!profile?.services) return null;
    
    return profile.services.slice(0, 2).map(service => (
      <Chip
        key={service}
        label={service}
        size="small"
        variant="outlined"
        sx={{ 
          ml: 0.5,
          height: 20,
          fontSize: '0.7rem',
          textTransform: 'capitalize',
        }}
      />
    ));
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: darkMode ? 'background.paper' : 'background.paper',
        color: 'text.primary',
        borderBottom: 1,
        borderColor: 'divider',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onMenuToggle}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Page Title & Breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              mr: 1,
              display: { xs: 'none', md: 'block' },
            }}
          >
            {getPageTitle()}
          </Typography>
          
          {!isMobile && getServiceBadges()}
        </Box>

        {/* Search Bar */}
        <Box sx={{ mr: 2, ml: 1 }} data-tour="search">
          <GlobalSearch token={token} profile={profile} />
        </Box>

        {/* Notifications */}
        <Box data-tour="notifications">
          <NotificationCenter token={token} />
        </Box>

        {/* Theme Toggle */}
        <Tooltip title={`Switch to ${darkMode ? 'light' : 'dark'} mode`}>
          <IconButton
            color="inherit"
            onClick={onThemeToggle}
            sx={{
              mx: 0.5,
              '&:hover': {
                backgroundColor: 'action.hover',
                transform: 'scale(1.05)',
              },
            }}
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>

        {/* Profile Menu */}
        <Tooltip title="Account settings">
          <IconButton
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
            sx={{
              ml: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Avatar
              src={profile?.profilePhoto}
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
              }}
            >
              {profile?.name?.charAt(0) || 'M'}
            </Avatar>
          </IconButton>
        </Tooltip>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          onClick={handleProfileMenuClose}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              minWidth: 200,
              borderRadius: 2,
              '& .MuiMenuItem-root': {
                borderRadius: 1,
                mx: 1,
                my: 0.5,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Profile Header */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {profile?.name || 'Mover Company'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {profile?.email || 'mover@example.com'}
            </Typography>
          </Box>
          
          <Divider />

          {/* Menu Items */}
          <MenuItem onClick={() => handleNavigation('/mover-portal/dashboard')}>
            <ListItemIcon>
              <DashboardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </MenuItem>

          <MenuItem onClick={() => handleNavigation('/mover-portal/profile')}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </MenuItem>

          <MenuItem onClick={() => handleNavigation('/mover-portal/settings')}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </MenuItem>

          <MenuItem onClick={() => handleNavigation('/mover-portal/help')}>
            <ListItemIcon>
              <HelpIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Help & Support" />
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText primary="Sign Out" />
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default EnhancedAppBar;